import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Paper,
  Stack,
  Container,
  CircularProgress,
  Modal,
  Button,
} from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import SubHeader from "../../layouts/subheader/SubHeader";
import { useParams } from "react-router-dom";
import {
  fetchTenantDetails
} from "../../apis/tenantApi/tenantApi";
import {
  getAllAppAdminPermissions,
  UpdateAllAppAdminPermissions,
  getAllAppAdminChildPermissions
} from "../../apis/applicationPermissionsApi/applicationPermissionsApi";

interface Permission {
  Id: string;
  permissionId?: string; // optional fallback
  defaultValue: string;
  permissionName: string;
  permissionDesc?: string;
  childPermissionType?: string;
  ChildItems?: Permission[];
}

const ApplicationPermissions = () => {
  const [domainURL, setDomainURL] = useState("");
  const [permissionData, setPermissionData] = useState<Permission[]>([]);
  const [isSubPermissions, setIsSubPermissions] = useState(false);
  const [currentPermissionName, setCurrentPermissionName] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [originalPermissions, setOriginalPermissions] = useState<Permission[]>([]);
  const [childPermissions, setChildPermissions] = useState<Permission[]>([]);
  const [childLoading, setChildLoading] = useState(false);
  const [currentSubPermissionName, setCurrentSubPermissionName] = useState("");

  const { id } = useParams();

  const fetchTenantDetailsAsync = async () => {
    const response = await fetchTenantDetails(Number(id));
    setDomainURL(response.client_data.domainURL);
  };

  const handleGetPermissions = async () => {
    const response = await getAllAppAdminPermissions(domainURL);
    const permissions = response.data;
    setPermissionData(permissions);
    setOriginalPermissions(permissions);
    setIsSubPermissions(false);
    setLoading(false);
  };

  const handleSwitchChange = (permission: Permission) => {
    setSelectedPermission(permission);
    setOpenModal(true);
  };

  const handleSettingsClick = (permission: Permission) => {
    if (permission.ChildItems) {
      setPermissionData(permission.ChildItems);
      setIsSubPermissions(true);
      setCurrentPermissionName(permission.permissionName);
    }
  };

  const handleSubPermissionClick = async (permission: Permission) => {
    setChildLoading(true);
    setCurrentSubPermissionName(permission.permissionName);
    try {
      const response = await getAllAppAdminChildPermissions(permission.Id, domainURL);
      const normalized = (Array.isArray(response.data) ? response.data : []).map((p: any) => ({
        ...p,
        Id: p.Id || p.permissionId, // Normalize Id
      }));
      setChildPermissions(normalized);
    } catch (e) {
      console.error(e);
      setChildPermissions([]);
    } finally {
      setChildLoading(false);
    }
  };

  const handleBackClick = () => {
    setPermissionData(originalPermissions);
    setIsSubPermissions(false);
    setCurrentPermissionName("");
    setChildPermissions([]);
  };

const updateLocalToggleState = (id: string | number) => {
  const idStr = String(id);

  // Helper function to update one list
  const updateValue = (list: Permission[]) =>
    list.map((p) =>
      String(p.Id) === idStr
        ? { ...p, defaultValue: p.defaultValue === "Y" ? "N" : "Y" }
        : p
    );

  // Update only the list where the permission exists
  if (permissionData.some((p) => String(p.Id) === idStr)) {
    setPermissionData((prev) => updateValue(prev));
  } else if (childPermissions.some((p) => String(p.Id) === idStr)) {
    setChildPermissions((prev) => updateValue(prev));
  }
};


  const handleUpdatePermissions = async () => {
    if (!selectedPermission || !(selectedPermission.Id || selectedPermission.permissionId)) {
      console.error("No valid permission selected");
      return;
    }

    const permissionId = Number(selectedPermission.Id || selectedPermission.permissionId);

    const payload = {
      Permission_Obj: [
        {
          Permission_Id: permissionId,
          Permission_Value: selectedPermission.defaultValue === "Y" ? "N" : "Y",
        },
      ],
    };

    console.log("Updating with payload:", payload);

    setConfirmLoading(true);
    await UpdateAllAppAdminPermissions(payload, domainURL);
    updateLocalToggleState(String(permissionId));
    setConfirmLoading(false);
    setOpenModal(false);
  };

  const handleCancel = () => {
    setSelectedPermission(null);
    setOpenModal(false);
  };

  const getGeneralAndFeaturePermissions = () => {
    const general = permissionData.filter(p => p.childPermissionType === "General");
    const features = permissionData.filter(p => p.childPermissionType === "Feature");
    return { general, features };
  };

  useEffect(() => {
    async function fetchData() {
      if (!isSubPermissions) {
        await fetchTenantDetailsAsync();
        if (!domainURL) return;
        await handleGetPermissions();
      }
    }
    fetchData();
  }, [domainURL]);

  return (
    <div className="tab-page-layout-1">
      <SubHeader title={`Application Configuration for Client ID: ${id} :: ${currentPermissionName}`} />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ maxHeight: "75vh", overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading...
              </Typography>
            </Box>
          ) : isSubPermissions ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Sub-permissions */}
              <Box sx={{ flex: 1, maxHeight: '70vh', overflowY: 'auto', pr: 1 }}>
                {(() => {
                  const { general, features } = getGeneralAndFeaturePermissions();
                  return (
                    <>
                      {[{ label: 'General', data: general }, { label: 'Features', data: features }].map(({ label, data }) => (
                        data.length > 0 && (
                          <Box key={label} sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>{label}</Typography>
                            <Stack spacing={2}>
                              {data.map((permission) => (
                                <Paper
                                  key={permission.Id}
                                  elevation={1}
                                  onClick={() => handleSubPermissionClick(permission)}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 2,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    "&:hover": { backgroundColor: "#f5f5f5" },
                                  }}
                                >
                                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography variant="subtitle1" fontWeight={600}>{permission.permissionName}</Typography>
                                    {permission.permissionDesc && (
                                      <Typography variant="body2" color="text.secondary">{permission.permissionDesc}</Typography>
                                    )}
                                  </Box>
                                  <Switch
                                    checked={permission.defaultValue === "Y"}
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSwitchChange(permission);
                                    }}
                                  />
                                </Paper>
                              ))}
                            </Stack>
                          </Box>
                        )
                      ))}
                    </>
                  );
                })()}
              </Box>

              {/* Child Permissions */}
              <Box sx={{ flex: 1, maxHeight: '70vh', overflowY: 'auto', pl: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {childLoading ? "Loading Child Permissions..." : `Child Permissions of: ${currentSubPermissionName}`}
                </Typography>
                {childLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {childPermissions.map((child) => (
                      <Paper
                        key={child.Id}
                        elevation={1}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="subtitle1" fontWeight={600}>{child.permissionName}</Typography>
                          {child.permissionDesc && (
                            <Typography variant="body2" color="text.secondary">{child.permissionDesc}</Typography>
                          )}
                        </Box>
                        <Switch
                          checked={child.defaultValue === "Y"}
                          color="primary"
                          onChange={() => handleSwitchChange(child)}
                        />
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ maxHeight: "75vh", overflowY: "auto", pr: 1 }}>
              {permissionData.map((permission) => (
                <Paper
                  key={permission.Id}
                  elevation={1}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderRadius: 2, mb: 2 }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle1" fontWeight={600}>{permission.permissionName}</Typography>
                    {permission.permissionDesc && (
                      <Typography variant="body2" color="text.secondary">{permission.permissionDesc}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Switch
                      checked={permission.defaultValue === "Y"}
                      onChange={() => handleSwitchChange(permission)}
                      color="primary"
                    />
                    <IconButton color="secondary" onClick={() => handleSettingsClick(permission)}>
                      <SettingsIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Container>

      {/* Modal */}
      <Modal open={openModal} onClose={handleCancel}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 400, bgcolor: "white", p: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedPermission?.defaultValue === "Y" ? "Disable" : "Enable"} Module
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedPermission?.defaultValue === "Y" ? "disable" : "enable"} the permission <strong>{selectedPermission?.permissionName}</strong>?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdatePermissions}
              disabled={confirmLoading}
            >
              {confirmLoading ? <CircularProgress size={24} color="inherit" /> : selectedPermission?.defaultValue === "Y" ? "Disable" : "Enable"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              disabled={confirmLoading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ApplicationPermissions;
